import type {
  UnderlyingHistoDispatchProps,
  UnderlyingHistoProps,
} from 'components/shared/UnderlyingHistos/UnderlyingHistoModal.connect';
import { UnderlyingHistoSkeleton } from 'components/shared/UnderlyingHistos/UnderlyingHistoSkeleton';
import { useToggleFeature } from 'hooks/useToggleFeature';
import type { EquityUnderlying } from 'models/underlying/equity';
import { FC, useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { HighStock } from './HighStock';
import { UnderlyingHistoModalButton } from './UnderlyingHistoModalButton';
import { GraphType } from './UnderlyingHistoModels';

export type UnderlyingHistoModalProps = {
  underlyings: EquityUnderlying[];
  isDisabled: boolean;
  isMulti: boolean;
} & UnderlyingHistoProps &
  UnderlyingHistoDispatchProps;

export const UnderlyingHistoModal: FC<UnderlyingHistoModalProps> = ({
  underlyings,
  underlyingsHistorics,
  isFetching,
  isFetchingFailed,
  errorMessage,
  isDisabled,
  isMulti,
  onUnderlyingHistoModalClicked,
}) => {
  const { formatMessage } = useIntl();
  const [isUnderlyingHistoModalDisplayed, setIsUnderlyingHistoModalDisplayed] = useState(false);
  const isActive = useToggleFeature('addUnderlyingHistoGraphParis');
  const toggleModal = useCallback(() => {
    setIsUnderlyingHistoModalDisplayed(!isUnderlyingHistoModalDisplayed);
  }, [isUnderlyingHistoModalDisplayed]);

  const bloombergTickerCodes = useMemo(() => underlyings.map(u => u.bloombergTickerCode), [underlyings]);

  const onUnderlyingHistoModalTrigger = () => {
    toggleModal();
    onUnderlyingHistoModalClicked(bloombergTickerCodes);
  };

  const onUnderlyingHistoModalTriggerRetry = () => {
    onUnderlyingHistoModalClicked(bloombergTickerCodes);
  };
  const title = useMemo(() => underlyings.map(u => u.name).join(', '), [underlyings]);

  const shouldDisplayGraph = !isFetching && !isFetchingFailed;

  return !isActive ? null : (
    <>
      <UnderlyingHistoModalButton isDisabled={isDisabled} isMulti={isMulti} onClick={onUnderlyingHistoModalTrigger} />
      <Modal
        backdrop="static"
        className="underlying-histo-modal-component"
        fade
        centered
        show={isUnderlyingHistoModalDisplayed}
        size="xl"
        onHide={toggleModal}
      >
        <ModalHeader onToggle={toggleModal} closeButton>
          <ModalTitle as="h4">
            <div className="d-flex">{formatMessage({ id: 'underlying-histograph-modal-header' })}</div>
          </ModalTitle>
        </ModalHeader>
        {isFetching && (
          <ModalBody className="py-0">
            <UnderlyingHistoSkeleton />
          </ModalBody>
        )}
        {shouldDisplayGraph && (
          <ModalBody className="py-0">
            <HighStock
              historics={underlyingsHistorics}
              graphType={isMulti ? GraphType.Multi : GraphType.Mono}
              title={title}
            />
            <br />
          </ModalBody>
        )}
        {isFetchingFailed && (
          <ModalBody className="py-0">
            <div className="m-3">
              <p>
                <h2>
                  {errorMessage === 'technical error'
                    ? formatMessage({ id: 'underlying-histo-technicalError.content' })
                    : formatMessage({ id: 'underlying-histo-functionalError.content' })}
                </h2>
              </p>
            </div>
            <br />
          </ModalBody>
        )}

        <ModalFooter>
          {isFetchingFailed && (
            <button
              className="btn btn-xl btn-default"
              data-e2e="retry-underlying-histo-modal"
              onClick={onUnderlyingHistoModalTriggerRetry}
            >
              {formatMessage({ id: 'underlying-histograph-retry' })}
            </button>
          )}
          <button className="btn btn-xl btn-default" data-e2e="close-underlying-histo-modal" onClick={toggleModal}>
            {formatMessage({ id: 'close' })}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

UnderlyingHistoModal.displayName = 'UnderlyingHistoModal';
